// src/db/tenantApi.js
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { writeBatch } from "firebase/firestore";

/**
 * Fetch all tenants
 */
export const getTenants = async () => {
  try {
    const snapshot = await getDocs(collection(db, "tenantmaster"));

    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error("Error fetching tenants:", error);
    throw error;
  }
};

/**
 * Add new tenant + tenant admin (appadmin)
 */
export const addTenantData = async (formData, createdBy) => {
  try {
    const companyName = formData.companyName?.trim();
    const appAdminName = formData.appAdminName?.trim();
    const appAdminEmail = formData.appAdminEmail?.trim();

    if (!companyName || !appAdminName || !appAdminEmail) {
      throw new Error("Company name and Tenant Admin details are required");
    }

    // Check if Tenant Admin email already exists
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("userEmailID", "==", appAdminEmail));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      throw new Error("A Tenant Admin with this email already exists");
    }

    // Create Tenant
    const tenantRef = await addDoc(collection(db, "tenantmaster"), {
      tenantName: companyName,
      tenantType: formData.tenantType || "",
      appAdminName: appAdminName,
      appAdminEmail: appAdminEmail,
      status: "active",
      createdAt: serverTimestamp(),
      createdBy
    });

    await updateDoc(tenantRef, {
      tenantId: tenantRef.id
    });

    // Create Tenant Admin user
    await addDoc(collection(db, "users"), {
      userName: appAdminName,
      userEmailID: appAdminEmail,
      tenantId: tenantRef.id,
      role: "appadmin",
      status: "pending_activation",
      createdAt: serverTimestamp(),
      createdBy
    });

    return tenantRef.id;

  } catch (error) {
    console.error("Error adding tenant:", error);
    throw error;
  }
};

/**
 * Update tenant details
 */
export const updateTenantData = async (tenantId, formData) => {
  try {
    if (!tenantId) throw new Error("Tenant ID required");

    await updateDoc(doc(db, "tenantmaster", tenantId), {
      tenantName: formData.companyName?.trim(),
      tenantType: formData.tenantType || "",
      appAdminName: formData.appAdminName?.trim(),
      appAdminEmail: formData.appAdminEmail?.trim(),
      updatedAt: serverTimestamp()
    });

    // If tenant is NOT mixed → update all facilities
    if (formData.tenantType !== 300) {
      // If tenant is NOT mixed → update all facilities
      const tenantType = Number(formData.tenantType);
      if (tenantType !== 300) {
        const facilitiesRef = collection(
          db,
          "tenantmaster",
          tenantId,
          "facilities"
        );

        const facilitiesSnapshot = await getDocs(facilitiesRef);
        const batch = writeBatch(db);

        facilitiesSnapshot.forEach((facilityDoc) => {

          const facilityRef = doc(
            db,
            "tenantmaster",
            tenantId,
            "facilities",
            facilityDoc.id
          );
          batch.update(facilityRef, {
            facilityType: tenantType
          });
        });
        await batch.commit();
      }
    }
  } catch (error) {
    console.error("Error updating tenant:", error);
    throw error;
  }
};

/**
 * Delete tenant
 */
export const deleteTenantData = async (tenantId) => {
  try {
    if (!tenantId) throw new Error("Tenant ID required");
    const batch = writeBatch(db);
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("tenantId", "==", tenantId));
    const snapshot = await getDocs(q);
    snapshot.forEach((docSnap) => {
      batch.delete(doc(db, "users", docSnap.id));
    });

    batch.delete(doc(db, "tenantmaster", tenantId));

    await batch.commit();

    console.log("Tenant and all associated users deleted successfully");

  } catch (error) {
    console.error("Error deleting tenant:", error);
    throw error;
  }
};

export const getTenantById = async (tenantId) => {
  try {

    if (!tenantId) return null;

    const snapshot = await getDocs(
      query(collection(db, "tenantmaster"), where("tenantId", "==", tenantId))
    );

    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];

    return {
      id: docSnap.id,
      ...docSnap.data()
    };

  } catch (error) {
    console.error("Error fetching tenant:", error);
    throw error;
  }
};